/**
 * @file This file implements a fractional index.
 *
 * This is based on https://observablehq.com/@dgreensp/implementing-fractional-indexing.
 */
import { Left, Right } from 'purify-ts';
import * as codec from 'purify-ts/Codec';
import * as parsers from './parsers';
/**
 * `POSSIBLE_DIGITS` is a string such as '0123456789' for base 10. Digits must be in ascending
 * character code order!
 */
const POSSIBLE_DIGITS = ' !"#$%&\'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~';
const SMALLEST_INTEGER = 'A                          ';
const INTEGER_ZERO = 'a ';
/**
 * Find a string between `a` and `b`.
 *
 * @param a The first string, may be empty.
 * @param b The second string, should be lexicographically greater than `a` if it is not null, may
 * not be empty.
 * @returns A string that is between `a` and `b`.
 */
function midpoint(a, b) {
    if (b !== null && a >= b) {
        throw new Error(`${a} >= ${b}`);
    }
    if (b != null && b !== '') {
        // remove longest common prefix.  pad `a` with 0s as we
        // go.  note that we don't need to pad `b`, because it can't
        // end before `a` while traversing the common prefix.
        let n = 0;
        while ((a[n] || POSSIBLE_DIGITS[0]) === b[n]) {
            n++;
        }
        if (n > 0) {
            return b.slice(0, n) + midpoint(a.slice(n), b.slice(n));
        }
    }
    // first digits (or lack of digit) are different
    const digitA = a ? POSSIBLE_DIGITS.indexOf(a[0]) : 0;
    const digitB = b !== null ? POSSIBLE_DIGITS.indexOf(b[0]) : POSSIBLE_DIGITS.length;
    if (digitB - digitA > 1) {
        const midDigit = Math.round(0.5 * (digitA + digitB));
        return POSSIBLE_DIGITS[midDigit];
    }
    else if (b != null && b.length > 1) {
        // first digits are consecutive
        return b.slice(0, 1);
    }
    else {
        // `b` is null or has length 1 (a single digit).
        // the first digit of `a` is the previous digit to `b`,
        // or 9 if `b` is null.
        // given, for example, midpoint('49', '5'), return
        // '4' + midpoint('9', null), which will become
        // '4' + '9' + midpoint('', null), which is '495'
        return POSSIBLE_DIGITS[digitA] + midpoint(a.slice(1), null);
    }
}
/**
 * Get the length of the integer part.
 *
 * @param head The head of the fractional index value.
 * @returns The lenght of the integer part.
 */
function getIntegerLength(head) {
    if (head >= 'a' && head <= 'z') {
        return head.charCodeAt(0) - 'a'.charCodeAt(0) + 2;
    }
    else if (head >= 'A' && head <= 'Z') {
        return 'Z'.charCodeAt(0) - head.charCodeAt(0) + 2;
    }
    else {
        throw new Error(`invalid order key head: ${head}`);
    }
}
/**
 * Get the integer part of the fractional index.
 *
 * @param key The fractional index to get the size of.
 * @returns The integer part of the fractional index.
 */
function getIntegerPart(key) {
    const integerPartLength = getIntegerLength(key[0]);
    if (integerPartLength > key.length) {
        throw new Error(`invalid order key: ${key}`);
    }
    return key.slice(0, integerPartLength);
}
/**
 * Validate the fractional index.
 *
 * @param key The fractional index to validate.
 */
function validateOrderKey(key) {
    if (key === SMALLEST_INTEGER) {
        throw new Error(`invalid order key: ${key}`);
    }
    // getIntegerPart will throw if the first character is bad,
    // or the key is too short.  we'd call it to check these things
    // even if we didn't need the result
    const i = getIntegerPart(key);
    const f = key.slice(i.length);
    if (f.slice(-1) === ' ') {
        throw new Error(`invalid order key: ${key}`);
    }
}
/**
 * Get the length of the integer part.
 *
 * Same as `getIntegerLength` but skips any validation, only call on already validated input.
 *
 * @param head The head of the fractional index value.
 * @returns The lenght of the integer part.
 */
function getIntegerLengthNoValidate(head) {
    if (head >= 'a' && head <= 'z') {
        return head.charCodeAt(0) - 'a'.charCodeAt(0) + 2;
    }
    else {
        return 'Z'.charCodeAt(0) - head.charCodeAt(0) + 2;
    }
}
/**
 * Get the integer part of the fractional index.
 *
 * Same as `getIntegerPart` but skips all validation, only call on already validated input.
 *
 * @param key The fractional index to get the size of.
 * @returns The integer part of the fractional index.
 */
function getIntegerPartNoValidate(key) {
    const integerPartLength = getIntegerLengthNoValidate(key[0]);
    return key.slice(0, integerPartLength);
}
/**
 * Increment the integer part that is contained in `x`.
 *
 * @param x The integer part to increment.
 * @returns The incremented integer, or null if `x` is the largest integer.
 */
function incrementInteger(x) {
    const [head, ...digs] = x.split('');
    let carry = true;
    for (let i = digs.length - 1; carry && i >= 0; i--) {
        const d = POSSIBLE_DIGITS.indexOf(digs[i]) + 1;
        if (d === POSSIBLE_DIGITS.length) {
            digs[i] = ' ';
        }
        else {
            digs[i] = POSSIBLE_DIGITS[d];
            carry = false;
        }
    }
    if (carry) {
        if (head === 'Z') {
            return 'a ';
        }
        // We are at the maximum integer already.
        if (head === 'z') {
            return null;
        }
        const h = String.fromCharCode(head.charCodeAt(0) + 1);
        if (h > 'a') {
            digs.push(' ');
        }
        else {
            digs.pop();
        }
        return h + digs.join('');
    }
    else {
        return head + digs.join('');
    }
}
/**
 * Decrement the integer part that is contained in `x`.
 *
 * @param x The integer part to decrement.
 * @returns The decremented integer, or null if `x` is the smallest integer.
 */
function decrementInteger(x) {
    const [head, ...digs] = x.split('');
    let borrow = true;
    for (let i = digs.length - 1; borrow && i >= 0; i--) {
        const d = POSSIBLE_DIGITS.indexOf(digs[i]) - 1;
        if (d === -1) {
            digs[i] = POSSIBLE_DIGITS.slice(-1);
        }
        else {
            digs[i] = POSSIBLE_DIGITS[d];
            borrow = false;
        }
    }
    if (borrow) {
        if (head === 'a') {
            return `Z${POSSIBLE_DIGITS.slice(-1)}`;
        }
        // We are at the smallest integer already.
        if (head === 'A') {
            return null;
        }
        const h = String.fromCharCode(head.charCodeAt(0) - 1);
        if (h < 'Z') {
            digs.push(POSSIBLE_DIGITS.slice(-1));
        }
        else {
            digs.pop();
        }
        return h + digs.join('');
    }
    else {
        return head + digs.join('');
    }
}
/** A class representing a fractional index. */
export class FractionalIndex {
    value;
    /**
     * Create a new fractional index.
     *
     * @param value The string that contains the actual fractional value. It is your responsibility
     * to verify the validity when constructing this class.
     */
    constructor(value) {
        this.value = value;
        Object.freeze(this);
    }
    /**
     * Check if `fi` is a fractional index.
     *
     * @param fi The value to check.
     * @returns If `fi` is a fractional index.
     */
    static isFractionalindex(fi) {
        return fi instanceof FractionalIndex;
    }
    /** @returns The int and fractional part of the fractional index. */
    get intAndFrac() {
        const ia = getIntegerPartNoValidate(this.value);
        const fa = this.value.slice(ia.length);
        return [ia, fa];
    }
    /**
     * Get a new fractional index so that `returned.isAfter(this)`.
     *
     * @returns A new fractional index that is bigger than we are.
     */
    getNext() {
        const [int, frac] = this.intAndFrac;
        const i = incrementInteger(int);
        return new FractionalIndex(i == null ? int + midpoint(frac, null) : i);
    }
    /**
     * Get a new fractional index so that `returned.isBefore(this)`.
     *
     * @returns A new fractional index that is smaller than we are.
     */
    getPrevious() {
        const int = getIntegerPartNoValidate(this.value);
        if (int === SMALLEST_INTEGER) {
            const frac = this.value.slice(int.length);
            return new FractionalIndex(int + midpoint('', frac));
        }
        if (int < this.value) {
            return new FractionalIndex(int);
        }
        const res = decrementInteger(int);
        if (res == null) {
            throw new Error('Cannot decrement any more');
        }
        return new FractionalIndex(res);
    }
    /**
     * Get a new fractional index between `other` and `this`.
     *
     * @param other The other index, can be after or before `this`.
     * @returns A new fractional (a) index so that `this CMP a CMP other` where `CMP` can be `>`
     * or `<` .
     */
    between(other) {
        if (other.isBefore(this)) {
            return other.between(this);
        }
        const [thisInt, thisFrac] = this.intAndFrac;
        const [otherInt, otherFrac] = other.intAndFrac;
        if (thisInt === otherInt) {
            return new FractionalIndex(thisInt + midpoint(thisFrac, otherFrac));
        }
        const newInt = incrementInteger(thisInt);
        if (newInt === null) {
            throw new Error('Cannot increment any more');
        }
        if (newInt < other.value) {
            return new FractionalIndex(newInt);
        }
        return new FractionalIndex(thisInt + midpoint(thisFrac, null));
    }
    /**
     * Check if we are after another index.
     *
     * @param other The other index to check that we are after.
     * @returns If `this` is after `other`.
     */
    isAfter(other) {
        return !(this.isBefore(other) || this.isEqual(other));
    }
    /**
     * Check if we are before another index.
     *
     * @param other The other index to check that we are before.
     * @returns If `this` is before `other`.
     */
    isBefore(other) {
        const otherValue = other.value;
        const meValue = this.value;
        const end = Math.min(meValue.length, otherValue.length);
        for (let i = 0; i < end; i++) {
            const meV = meValue[i];
            const otherV = otherValue[i];
            if (meV !== otherV) {
                return meV < otherV;
            }
        }
        return meValue.length < otherValue.length;
    }
    /**
     * Check if we are equal to another fractional index.
     *
     * @param other The other to check if we are equal to.
     * @returns If `this` and `other` should be considered equal.
     */
    isEqual(other) {
        return this.value === other.value;
    }
    /** @returns The starting fractional index, which is approx in the middle. */
    static createInitial() {
        return new FractionalIndex(INTEGER_ZERO);
    }
    /** @returns The encoded value of this fractional index. */
    encode() {
        return this.value;
    }
    /**
     * Create a new fractional index.
     *
     * @param value The value to create the fractional index from.
     * @returns Either an error, as the value may not be valid, or a fresh fractional index.
     */
    static fromString(value) {
        try {
            validateOrderKey(value);
        }
        catch {
            return Left(`Could not parse ${value} as fractional index`);
        }
        return Right(new FractionalIndex(value));
    }
}
/**
 * Create a new fractional index parser.
 *
 * @param baseParsers The base parsers to use for the new parser.
 * @returns A parser for fractional indexes.
 */
export function makeFractionalIndexParser(baseParsers) {
    const str = baseParsers.string;
    return codec.Codec.custom({
        decode: val => str.decode(val).chain(FractionalIndex.fromString),
        encode: val => str.encode(val.encode()),
    });
}
/** A fractional index parser that uses the default parsers. */
export const FractionalIndexParser = makeFractionalIndexParser(parsers.DEFAULT_BASE_PARSERS);
/**
 * Creates an increasing index generator.
 *
 * The generator only goes in ascending order. It starts from `createInitial()`
 * and generates always calling `getNext`.
 *
 * @returns An index generator.
 */
export function makeIndexGetter() {
    let index = FractionalIndex.createInitial();
    return () => {
        const ret = index;
        index = index.getNext();
        return ret;
    };
}
